import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Link from '@mui/material/Link';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';

import { setLanguage } from '@common/Util';

import { accessibilityStatementContent } from './Constants';

const AddressLine = ({ line, linkColor }) => {
  if (typeof line === 'string') {
    return <Typography>{line}</Typography>;
  }

  return (
    <Typography>
      {`${line.label}: `}
      {line.href ? (
        <Link
          href={line.href}
          sx={{ color: linkColor }}
          {...(line.external && { target: '_blank', rel: 'noopener noreferrer' })}
        >
          {line.text}
        </Link>
      ) : (
        line.text
      )}
    </Typography>
  );
};

const ContentBlock = ({ block, linkColor }) => {
  switch (block.type) {
    case 'list':
      return (
        <Box component='ul' sx={{ mt: 0, mb: 2, pl: 3 }}>
          {block.items.map((item) => (
            <Typography component='li' key={item}>
              {item}
            </Typography>
          ))}
        </Box>
      );
    case 'address':
      return (
        <Box sx={{ mb: 2 }}>
          {block.lines.map((line) => (
            <AddressLine
              key={typeof line === 'string' ? line : line.text}
              line={line}
              linkColor={linkColor}
            />
          ))}
        </Box>
      );
    default:
      return <Typography sx={{ mb: 2 }}>{block.text}</Typography>;
  }
};

const AccessibilityStatement = () => {
  const theme = useTheme();

  const language = setLanguage().toLowerCase();
  const { title, subtitle, sections } =
    accessibilityStatementContent[language] || accessibilityStatementContent.en;

  const linkColor = theme.palette.primary.contrastText;

  return (
    <Container
      maxWidth='md'
      sx={{ mt: 4, mb: 4, minHeight: '90vh' }}
      data-testid='accessibilityStatementContainer'
    >
      <Typography variant='h5' component='h1' sx={{ fontWeight: 'bold' }}>
        {title}
      </Typography>
      <Typography variant='subtitle1' sx={{ mb: 3 }}>
        {subtitle}
      </Typography>
      {sections.map((section) => (
        <Box component='section' key={section.heading}>
          <Typography
            variant='h6'
            component='h2'
            sx={{ mt: '1rem', mb: '0.5rem', color: linkColor }}
          >
            {section.heading}
          </Typography>
          {section.content.map((block, index) => (
            <ContentBlock key={index} block={block} linkColor={linkColor} />
          ))}
        </Box>
      ))}
    </Container>
  );
};

export default AccessibilityStatement;
